import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CardHeader,
  Container,
  EmptyContent,
  Text,
  Tooltip,
} from '@/components/ui';
import { DataTable } from '@/components/data-table';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useTablePageSize } from '@/hooks/useTablePageSize';
import {
  fetchEventTrails,
  selectAuditTrailData,
  selectAuditTrailError,
  selectAuditTrailPagination,
  selectAuditTrailState,
} from '@/store/slices/auditTrailSlice';
import { EventTrailEntry } from '@/types/eventTrails';
import { withPermissions } from '@/components/withPermissions';
import { PERMISSIONS } from '@/types/permissions';
import { formatFileSize } from '@/utils/fileUtils';
import { EventDetailsModal } from './EventDetailsModal';

import {
  IconEye,
  IconHistory,
  IconLock,
  IconRefresh,
} from '@tabler/icons-react';
import { ColumnDef } from '@tanstack/react-table';

const AuditTrailPageComponent: React.FC = () => {
  const dispatch = useAppDispatch();

  // Store selectors
  const data = useAppSelector(selectAuditTrailData);
  const pagination = useAppSelector(selectAuditTrailPagination);
  const state = useAppSelector(selectAuditTrailState);
  const error = useAppSelector(selectAuditTrailError);

  // Table state
  const [globalFilter, setGlobalFilter] = useState('');

  // Persisted global table page size (shared across tables)
  const {
    pageSize: preferredPageSize,
    setPageSize: setPreferredPageSize,
  } = useTablePageSize(100);

  // Modal state
  const [selectedEvent, setSelectedEvent] = useState<EventTrailEntry | null>(
    null
  );
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  // Load data on component mount
  useEffect(() => {
    dispatch(
      fetchEventTrails({
        page: 1,
        per_page: preferredPageSize,
        order: 'created_at',
        order_direction: 'desc',
      })
    );
    // Intentionally run once; subsequent page-size changes are handled by pagination callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleShowDetails = (event: EventTrailEntry) => {
    setSelectedEvent(event);
    setDetailsModalOpen(true);
  };

  const handlePaginationChange = useCallback(
    (next: { pageIndex: number; pageSize: number }) => {
      // Keep global page size preference in sync.
      setPreferredPageSize(next.pageSize);

      dispatch(
        fetchEventTrails({
          page: next.pageIndex + 1,
          per_page: next.pageSize,
          order: 'created_at',
          order_direction: 'desc',
        })
      );
    },
    [dispatch, setPreferredPageSize]
  );

  const paginationConfig = useMemo(
    () => ({
      pageIndex: Math.max((pagination?.current_page ?? 1) - 1, 0),
      pageSize: pagination?.per_page ?? preferredPageSize,
      pageCount: pagination?.total_pages ?? 0,
      totalCount: pagination?.total_count ?? 0,
      onPaginationChange: handlePaginationChange,
    }),
    [
      pagination?.current_page,
      pagination?.per_page,
      pagination?.total_pages,
      pagination?.total_count,
      preferredPageSize,
      handlePaginationChange,
    ]
  );

  const handleRefresh = useCallback(async () => {
    await dispatch(
      fetchEventTrails({
        page: pagination?.current_page || 1,
        per_page: pagination?.per_page || preferredPageSize,
        order: 'created_at',
        order_direction: 'desc',
      })
    ).unwrap();
  }, [dispatch, pagination, preferredPageSize]);

  const columns: ColumnDef<EventTrailEntry>[] = useMemo(
    () => [
      {
        accessorKey: 'created_at',
        header: 'Date & Time',
        meta: {
          cell: {
            variant: 'datetime',
            relative: false,
          },
          size: 250,
        },
        enableSorting: true,
      },
      {
        accessorKey: 'user',
        header: 'User',
        meta: {
          cell: {
            variant: 'user',
            showEmail: true,
          },
          minSize: 200,
          maxSize: 300,
        },
        enableSorting: true,
      },
      {
        accessorKey: 'action',
        header: 'Action',
        meta: {
          cell: {
            variant: 'action',
          },
          minSize: 150,
          maxSize: '0.5fr',
        },
        enableSorting: true,
      },
      {
        accessorKey: 'data',
        header: 'File Details',
        meta: {
          cell: {
            variant: 'custom',
            render: (_value: unknown, row: EventTrailEntry) => {
              const data = row.data;
              if (!data?.filename) {
                return <Text size="xsmall">-</Text>;
              }

              const tooltipContent = [
                data.filename && `File: ${data.filename}`,
                data.size && `Size: ${formatFileSize(data.size)}`,
                data.content_type && `Type: ${data.content_type}`,
                data.checksum && `Checksum: ${data.checksum}`,
              ]
                .filter(Boolean)
                .join('\n');

              return (
                <Tooltip content={tooltipContent}>
                  <Text
                    size="2xsmall"
                    style={{ fontFamily: 'var(--font-mono)', cursor: 'help' }}
                  >
                    {data.filename}
                  </Text>
                </Tooltip>
              );
            },
          },
          minSize: 150,
          maxSize: '1fr',
        },
        enableSorting: false,
      },
      {
        accessorKey: 'entity_type',
        header: 'Entity',
        meta: {
          cell: {
            variant: 'text',
          },
          minSize: 150,
          maxSize: '1fr',
        },
        enableSorting: true,
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => (
          <Button
            // isIconOnly
            variant="text"
            size="xsmall"
            onClick={() => handleShowDetails(row.original)}
            startIcon={<IconEye />}
          >
            Show Details
          </Button>
        ),
        enableSorting: false,
        meta: {
          size: 'auto',
          systemColumn: true,
        },
      },
    ],
    []
  );

  // Custom empty content for when no audit trail entries exist
  const customEmptyContent = (
    <EmptyContent
      style={{ alignSelf: 'center', minHeight: '400px' }}
      withIconBox
      icon={<IconHistory style={{ color: 'var(--blue-9)' }} />}
      title="No audit trail entries yet"
      description="System activities and user actions will appear here. Start using the system to see audit trail data."
      size="large"
    />
  );

  // Right slot for DataTable toolbar with refresh button
  const rightSlot = useMemo(
    () => (
      <Button
        variant="outlined"
        size="small"
        onClick={handleRefresh}
        startIcon={<IconRefresh />}
        disabled={state === 'loading'}
      >
        Refresh
      </Button>
    ),
    [state, handleRefresh]
  );

  return (
    <Container pt="4" pb="4" size="expand" className="audit-trail-page">
      {/* Header is always shown */}
      <CardHeader
        cardTitle="Audit Trail"
        description="Track all system activities and user actions"
      />

      {/* Always show DataTable with custom empty content */}
      <DataTable
        scrollMode="table"
        data={data || []}
        columns={columns}
        loading={state === 'loading'}
        showSkeletonOnLoading={true}
        skeletonRows={20}
        enableSorting={true}
        enableFiltering={true}
        enablePagination={true}
        enableColumnVisibility={true}
        enableRowSelection={false}
        manualPagination={true}
        searchPlaceholder="Search audit trail..."
        pageSizeOptions={[10, 25, 50, 100]}
        emptyMessage="No audit trail entries found matching your search."
        toolbarRightSlot={rightSlot}
        pagination={paginationConfig}
        customEmptyContent={
          state === 'error' ? (
            <EmptyContent
              state="error"
              size="large"
              icon={<IconHistory />}
              withIconBox
              title="Error Loading Audit Trail"
              description={
                error || 'Something went wrong while loading audit trail data.'
              }
            >
              <Button
                onClick={() =>
                  dispatch(
                    fetchEventTrails({
                      page: 1,
                      per_page: preferredPageSize,
                      order: 'created_at',
                      order_direction: 'desc',
                    })
                  )
                }
                variant="classic"
                color="secondary"
              >
                Try Again
              </Button>
            </EmptyContent>
          ) : state === 'forbidden' ? (
            <EmptyContent
              size="large"
              icon={<IconLock />}
              withIconBox
              title="Access Denied"
              description="You do not have permission to view the audit trail."
            />
          ) : (
            customEmptyContent
          )
        }
        getRowId={(row: EventTrailEntry) => row.id.toString()}
        filtering={{
          columnFilters: [],
          globalFilter,
          onGlobalFilterChange: setGlobalFilter,
        }}
      />

      {/* Event Details Modal */}
      <EventDetailsModal
        event={selectedEvent}
        open={detailsModalOpen}
        onOpenChange={setDetailsModalOpen}
      />
    </Container>
  );
};

export const AuditTrailPage = withPermissions(AuditTrailPageComponent, {
  requiredPermissions: [PERMISSIONS.AUDIT_TRAIL_ACCESS],
  showRoleSwitcher: true,
});
